import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as adminService from "@/shared/api/services/adminService";
import * as transactionService from "@/shared/api/services/transactionService";
import * as merchantService from "@/shared/api/services/merchantService";
import { subDays, startOfDay, endOfDay } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  ArrowLeft, Pencil, Building2, TrendingUp, ArrowDownToLine, ArrowUpFromLine, CheckCircle2, XCircle, Clock, Activity, Key,
  Percent, AlertTriangle, RefreshCw,
} from "lucide-react";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import DrillDownBreadcrumb from "@/shared/components/admin/DrillDownBreadcrumb";
import DetailStatCard from "@/shared/components/admin/DetailStatCard";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";
import OverviewTab from "@/shared/components/admin/merchantDetail/OverviewTab";
import TransactionsListTab from "@/shared/components/admin/merchantDetail/TransactionsListTab";
import SettingsTab from "@/shared/components/admin/merchantDetail/SettingsTab";

// This page uses the default admin layout, so no custom layout wrapper needed

const statusConfig = {
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  suspended: { icon: AlertTriangle, color: "bg-red-100 text-red-700", label: "Suspended" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
  terminated: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "Terminated" },
};

export default function AdminMerchantDetail() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const merchantId = searchParams.get("id");

  const [showApiKey, setShowApiKey] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    type: "all",
    dateFrom: null,
    dateTo: null,
  });
  const [page, setPage] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pageSize = 10;

  const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  // Queries

  // Fetch merchant details
  const { data: merchant } = useQuery({
    queryKey: ["merchant-detail", merchantId],
    queryFn: async () => adminService.getMerchantById(merchantId),
    enabled: !!merchantId,
  });

  // Merchant transactions (paginated)
  const { data: paginationResult, isLoading: txLoading, refetch: refetchTransactions } = useQuery({
    queryKey: ["merchant-transactions", merchantId, page, pageSize, filters],
    queryFn: () => {
      const backendFilters = {};
      if (filters.status !== "all") backendFilters.status = filters.status;
      if (filters.type !== "all") backendFilters.type = filters.type;
      if (filters.dateFrom) backendFilters.startDate = filters.dateFrom.toISOString();
      if (filters.dateTo) {
        const endOfToDay = new Date(filters.dateTo);
        endOfToDay.setHours(23, 59, 59, 999);
        backendFilters.endDate = endOfToDay.toISOString();
      }
      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        filters.search,
        backendFilters,
        [merchantId]
      );
    },
    enabled: !!merchantId,
    keepPreviousData: true,
  });

  const transactions = mapGlobalTransactions(paginationResult?.data || []);
  const totalTransactions = paginationResult?.total || 0;
  const totalPages = paginationResult?.totalPages || 1;

  // Recent transactions for overview
  const { data: recentPaginationResult } = useQuery({
    queryKey: ["merchant-recent-transactions", merchantId],
    queryFn: () => transactionService.getPaginatedTransactions(1, 5, "", {}, [merchantId]),
    enabled: !!merchantId,
  });
  const recentTransactions = mapGlobalTransactions(recentPaginationResult?.data || []);

  // Merchant stats
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['merchant-stats', merchantId],
    queryFn: async () => adminService.getMerchantDashboardLiveMetrics(merchantId),
    enabled: !!merchantId,
  });

  // Volume chart (fixed last 7 days)
  const chartStartISO = startOfDay(subDays(new Date(), 6)).toISOString();
  const chartEndISO = endOfDay(new Date()).toISOString();
  const chartCurrency = merchant?.currency || "PKR";

  const { data: chartDataResult } = useQuery({
    queryKey: ["merchant-transactions-chart", merchantId, chartCurrency, chartStartISO, chartEndISO],
    queryFn: () => merchantService.getTransactionsChart(chartStartISO, chartEndISO, chartCurrency, [merchantId]),
    enabled: !!merchantId,
    placeholderData: (prev) => prev,
  });
  const chartData = chartDataResult || [];

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refetchTransactions(), refetchStats()]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "all",
      type: activeTab === "overview" ? "all" : (activeTab === "deposits" ? "deposit" : "withdrawal"),
      dateFrom: null,
      dateTo: null,
    });
    setPage(0);
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setPage(0);
    let newType = "all";
    if (value === "deposits") newType = "deposit";
    if (value === "withdrawals") newType = "withdrawal";
    setFilters(prev => ({ ...prev, type: newType }));
  };

  const merchantStatusKey = merchant?.status
    ? merchant.status.toString().toLowerCase()
    : "pending";
  const status = statusConfig[merchantStatusKey] || statusConfig.pending;
  const StatusIcon = status.icon;

  const currencySymbol = merchant?.currency === "PKR" ? "Rs" : "฿";

  const merchantStatusLabel = merchant?.status
    ? (t && t(merchant.status) !== merchant.status ? t(merchant.status) : status.label)
    : status.label;

  const statCards = [
    {
      title: t ? t("todaysVolume") : "Today's Volume",
      value: `${currencySymbol}${toNumber(stats?.todayVolume).toLocaleString()}`,
      icon: TrendingUp,
      color: "bg-blue-100 text-blue-600",
    },
    {
      title: t ? t("totalVolume") : "Total Volume",
      value: `${currencySymbol}${toNumber(stats?.totalVolume).toLocaleString()}`,
      icon: Activity,
      color: "bg-emerald-100 text-emerald-600",
    },
    {
      title: t ? t("successRate") : "Success Rate",
      value: `${toNumber(stats?.successRate).toFixed(2)}%`,
      icon: Percent,
      color: "bg-purple-100 text-purple-600",
    },
    {
      title: t ? t("totalWithdrawals") : "Total Withdrawals",
      value: `${currencySymbol}${toNumber(stats?.totalWithdrawals).toLocaleString()}`,
      icon: ArrowUpFromLine,
      color: "bg-amber-100 text-amber-600",
    },
    {
      title: t ? t("totalDeposits") : "Total Deposits",
      value: `${currencySymbol}${toNumber(stats?.totalDeposits).toLocaleString()}`,
      icon: ArrowDownToLine,
      color: "bg-sky-100 text-sky-600",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link to={createPageUrl("Merchants")}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center">
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">
                  {merchant?.name || merchant?.code || (t ? t("merchant") : "Merchant")}
                </h1>
                <Badge className={`${status.color} gap-1 border-0`}>
                  <StatusIcon className="h-3 w-3" />
                  {merchantStatusLabel}
                </Badge>
              </div>
              <p className="text-slate-500">
                {merchant?.code || "N/A"} • {merchant?.email || "N/A"} • {merchant?.currency || ""}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {t ? t("refresh") : "Refresh"}
          </Button>
          <Link to={createPageUrl("Merchants") + `?editId=${merchantId}`}>
            <Button className="gap-2">
              <Pencil className="h-4 w-4" />
              {t ? t("editMerchant") : "Edit Merchant"}
            </Button>
          </Link>
        </div>
      </div>

      {/* Drill Down Breadcrumb */}
      <DrillDownBreadcrumb engine={null} reseller={null} merchant={merchant} />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, i) => (
          <DetailStatCard key={stat.title} {...stat} delay={i * 0.05} />
        ))}
      </div>

      <Tabs defaultValue="overview" onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" className="gap-2"><Activity className="h-4 w-4" />{t ? t("overview") : "Overview"}</TabsTrigger>
          <TabsTrigger value="deposits" className="gap-2"><ArrowDownToLine className="h-4 w-4" />{t ? t("deposits") : "Deposits"}</TabsTrigger>
          <TabsTrigger value="withdrawals" className="gap-2"><ArrowUpFromLine className="h-4 w-4" />{t ? t("withdrawals") : "Withdrawals"}</TabsTrigger>
          <TabsTrigger value="settings" className="gap-2"><Key className="h-4 w-4" />{t ? t("apiAndSettings") : "API & Settings"}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab
            merchant={merchant}
            recentTransactions={recentTransactions}
            txLoading={txLoading}
            onView={setSelectedTx}
            chartData={chartData}
            chartCurrencySymbol={currencySymbol}
            t={t}
          />
        </TabsContent>

        <TabsContent value="deposits">
          <TransactionsListTab
            transactions={transactions}
            txLoading={txLoading}
            filters={filters}
            setFilters={setFilters}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            totalTransactions={totalTransactions}
            totalPages={totalPages}
            onView={setSelectedTx}
            onClearFilters={clearFilters}
            t={t}
          />
        </TabsContent>

        <TabsContent value="withdrawals">
          <TransactionsListTab
            transactions={transactions}
            txLoading={txLoading}
            filters={filters}
            setFilters={setFilters}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            totalTransactions={totalTransactions}
            totalPages={totalPages}
            onView={setSelectedTx}
            onClearFilters={clearFilters}
            t={t}
          />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsTab
            merchant={merchant}
            showApiKey={showApiKey}
            setShowApiKey={setShowApiKey}
            t={t}
          />
        </TabsContent>
      </Tabs>

      {selectedTx && (
        <TransactionDetailPanel
          transaction={selectedTx}
          open={Boolean(selectedTx)}
          onClose={() => setSelectedTx(null)}
          isQRCode={selectedTx.type !== "withdrawal"}
        />
      )}
    </div>
  );
}
